import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1100, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, marginBottom: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8, fontWeight: 600 };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8, fontWeight: 500 };
const btnInfo = { padding: '8px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 };

const RAW_BASE = process.env.REACT_APP_API_BASE || '/api';
const API_BASE = RAW_BASE.replace(/["']/g, '').replace(/\/+$/, '');

export default function ExamFlowBuilder() {
  const navigate = useNavigate();
  
  const [examName, setExamName] = useState('');
  const [level, setLevel] = useState('');
  const [availableBands, setAvailableBands] = useState([]);
  const [startBand, setStartBand] = useState('');
  const [endBand, setEndBand] = useState('');
  
  const [passingPercentage, setPassingPercentage] = useState(60);
  const [primaryPoints, setPrimaryPoints] = useState(1);
  const [backupPoints, setBackupPoints] = useState(0.5);
  const [backupCount, setBackupCount] = useState(1);

  const [bandData, setBandData] = useState({});
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // 1️⃣ Fetch available bands when level changes
  useEffect(() => {
    if (level) {
      fetch(`${API_BASE}/admin/flow-builder/bands?level=${level}`)
        .then(res => res.json())
        .then(data => {
          const bands = data.bands || [];
          setAvailableBands(bands);
          if (bands.length > 0) {
            setStartBand(bands[0]);
            setEndBand(bands[bands.length - 1]);
          }
        })
        .catch(err => setError(`Failed to load bands: ${err.message}`));
    }
  }, [level]);

  // 2️⃣ Fetch tag combos when range changes
  useEffect(() => {
    if (!level || !startBand || !endBand || availableBands.length === 0) return;

    const sIdx = availableBands.indexOf(startBand);
    const eIdx = availableBands.indexOf(endBand);
    const validStart = availableBands[Math.min(sIdx, eIdx)];
    const validEnd = availableBands[Math.max(sIdx, eIdx)];

    setLoading(true); setError('');
    const url = `${API_BASE}/admin/flow-builder/tag-combos?level=${level}&start_band=${validStart}&end_band=${validEnd}`;
    
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setBandData(data.bandData || {});
        const newRules = {};
        const rangeBands = availableBands.slice(Math.min(sIdx, eIdx), Math.max(sIdx, eIdx) + 1);
        rangeBands.forEach(band => {
          newRules[band] = (data.bandData?.[band] || []).map(c => ({ combo: c.combo, quantity: 1 }));
        });
        setRules(newRules);
      })
      .catch(err => setError(`Failed to load tags: ${err.message}`))
      .finally(() => setLoading(false));
  }, [level, startBand, endBand, availableBands]);

  const updateRule = (band, idx, val) => {
    setRules(prev => ({
      ...prev,
      [band]: prev[band].map((r, i) => i === idx ? { ...r, quantity: Math.max(0, parseInt(val) || 0) } : r)
    }));
  };

  const handleGenerate = async () => {
    if (!examName.trim()) return setError('Exam name is required');
    if (!level) return setError('Level is required');

    const sIdx = availableBands.indexOf(startBand);
    const eIdx = availableBands.indexOf(endBand);
    const activeRules = [];
    
    availableBands.slice(Math.min(sIdx, eIdx), Math.max(sIdx, eIdx) + 1).forEach(band => {
      const combos = rules[band]?.filter(r => r.quantity > 0) || [];
      if (combos.length > 0) activeRules.push({ band, combos });
    });

    if (activeRules.length === 0) return setError('Select at least one tag combo with quantity > 0');

    setGenerating(true); setError(''); setResult(null);
    
    try {
      const res = await fetch(`${API_BASE}/admin/exams/flow-builder`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          exam_name: examName.trim(),
          level, 
          start_band: availableBands[Math.min(sIdx, eIdx)], 
          end_band: availableBands[Math.max(sIdx, eIdx)],
          passing_percentage: passingPercentage,
          primary_points: primaryPoints,
          backup_points: backupPoints,
          backup_count: backupCount,
          band_rules: activeRules
        })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const calculateEstimates = () => {
    let totalPrim = 0, totalBack = 0;
    const sIdx = availableBands.indexOf(startBand);
    const eIdx = availableBands.indexOf(endBand);
    availableBands.slice(Math.min(sIdx, eIdx), Math.max(sIdx, eIdx) + 1).forEach(band => {
      const qty = rules[band]?.reduce((sum, r) => sum + (r.quantity || 0), 0) || 0;
      totalPrim += qty;
      totalBack += qty * backupCount;
    });
    return { totalPrim, totalBack, total: totalPrim + totalBack };
  };

  const { totalPrim, totalBack, total } = calculateEstimates();

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>🔀 Advanced English Flow Builder</h2>
        <button onClick={() => navigate('/admin/flow-builder-exams')} style={btnSecondary}>← Back to Exams</button>
      </div>

      {error && <div style={{ padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15, border: '1px solid #f5c6cb' }}>❌ {error}</div>}
      
      {result ? (
        <div style={{ padding: 30, background: '#d4edda', borderRadius: 8, textAlign: 'center', border: '1px solid #c3e6cb' }}>
          <h3 style={{ color: '#155724', marginTop: 0 }}>✅ Exam Created Successfully!</h3>
          <p style={{ fontSize: 16, color: '#155724' }}><strong>{result.count} questions</strong> generated with multi-band progression.</p>
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', gap: 10 }}>
            <button onClick={() => navigate('/admin/flow-builder-exams')} style={btnInfo}>Preview Exam</button>
            <button onClick={() => navigate('/admin/flow-builder-exams')} style={btnSecondary}>View All Exams</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
            <h3 style={{ marginTop: 0, marginBottom: 15 }}>1. Exam Settings & Scoring</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 15 }}>
              <div>
                <label style={labelStyle}>Exam Name</label>
                <input style={inputStyle} value={examName} onChange={e => setExamName(e.target.value)} placeholder="e.g., English Level 3 Diagnostic" />
              </div>
              <div>
                <label style={labelStyle}>Level</label>
                <select style={inputStyle} value={level} onChange={e => setLevel(e.target.value)}>
                  <option value="">Select Level</option>
                  {Array.from({length: 12}, (_, i) => (i + 1).toString()).map(l => (
                    <option key={l} value={l}>Level {l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Band Range</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select style={inputStyle} value={startBand} onChange={e => setStartBand(e.target.value)} disabled={availableBands.length === 0}>
                    {availableBands.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <span style={{ color: '#666', fontWeight: 'bold' }}>→</span>
                  <select style={inputStyle} value={endBand} onChange={e => setEndBand(e.target.value)} disabled={availableBands.length === 0}>
                    {availableBands.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 15, marginTop: 15 }}>
              <div>
                <label style={labelStyle}>Passing %</label>
                <input type="number" style={inputStyle} value={passingPercentage} onChange={e => setPassingPercentage(Math.min(100, Math.max(0, Number(e.target.value))))} />
              </div>
              <div>
                <label style={labelStyle}>Primary Q Points</label>
                <input type="number" step="0.1" style={inputStyle} value={primaryPoints} onChange={e => setPrimaryPoints(Math.max(0, Number(e.target.value)))} />
              </div>
              <div>
                <label style={labelStyle}>Backup Q Points</label>
                <input type="number" step="0.1" style={inputStyle} value={backupPoints} onChange={e => setBackupPoints(Math.max(0, Number(e.target.value)))} />
              </div>
              <div>
                <label style={labelStyle}>Backups per Wrong</label>
                <select style={inputStyle} value={backupCount} onChange={e => setBackupCount(Number(e.target.value))}>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </select>
              </div>
            </div>
          </div>

          {level && availableBands.length > 0 && (
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                <h3 style={{ margin: 0 }}>2. Questions per Band & Tag</h3>
                {loading && <span style={{ color: '#007BFF', fontSize: 14, fontWeight: 500 }}>⏳ Loading tags...</span>}
              </div>

              <div style={{ marginBottom: 20, padding: 12, background: '#e7f1ff', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14 }}>
                <span><strong>Estimated Total:</strong> {totalPrim} primary + {totalBack} backup = <strong style={{ color: '#0056b3' }}>{total} questions</strong></span>
                <span style={{ color: '#0056b3' }}>Pass: ≥{passingPercentage}% | Primary: {primaryPoints}pt | Backup: {backupPoints}pt</span>
              </div>
              
              {availableBands.slice(Math.min(availableBands.indexOf(startBand), availableBands.indexOf(endBand)), Math.max(availableBands.indexOf(startBand), availableBands.indexOf(endBand)) + 1).map(band => (
                <div key={band} style={{ marginBottom: 20, padding: 15, border: '1px solid #eee', borderRadius: 8, background: '#fafbfc' }}>
                  <h4 style={{ marginTop: 0, color: '#007BFF', borderBottom: '1px solid #e2e6ea', paddingBottom: 8 }}>{band} Band</h4>
                  {(bandData[band] || []).length === 0 && <p style={{ color: '#666', fontSize: 13, fontStyle: 'italic' }}>No tag combinations found for this band.</p>}
                  {(bandData[band] || []).length > 0 && (
                    <div style={{ maxHeight: 250, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, background: '#fff' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                          <tr>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Tag Combo</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Available</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rules[band]?.map((rule, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '10px' }}>
                                {rule.combo.split('+').map(t => (
                                  <span key={t} style={{ display: 'inline-block', padding: '2px 6px', background: '#e2e6ea', borderRadius: 4, fontSize: 11, margin: '0 2px', fontFamily: 'monospace' }}>
                                    {t}
                                  </span>
                                ))}
                              </td>
                              <td style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                                {bandData[band]?.find(c => c.combo === rule.combo)?.count || 0}
                              </td>
                              <td style={{ padding: '10px', textAlign: 'center' }}>
                                <input 
                                  type="number" 
                                  min="0" 
                                  style={{ ...inputStyle, width: 70, margin: '0 auto', textAlign: 'center' }} 
                                  value={rule.quantity} 
                                  onChange={e => updateRule(band, idx, e.target.value)} 
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button 
              onClick={handleGenerate} 
              disabled={generating || !examName || !level || availableBands.length === 0} 
              style={{ 
                ...btnPrimary, 
                opacity: (generating || !examName || !level || availableBands.length === 0) ? 0.6 : 1,
                padding: '12px 24px',
                fontSize: 16
              }}
            >
              {generating ? '🔄 Generating Exam...' : `🚀 Generate Exam (~${total} Qs)`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}